import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CheckIcon,
  ConfirmationModal,
  GKButton,
  GKClientDataGrid,
  GKModal,
  Grid2,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { FeePlansForm } from "interfaces/Revenue";
import { useState } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, refetchQuery } from "utils/helpers";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const MapPortfolioModal = (props: Props) => {
  const { open, setOpen } = props;

  const feePlanDetailsForm = useFormContext<FeePlansForm>();

  const [confirmationModal, setConfirmationModal] = useState(false);

  const {
    data: portfolioFeePlanMappingList,
    isFetching: portfolioMappingFetching,
  } = useFetchQuery({
    key: ["PORTFOLIO_FEE_PLAN_MAPPING"],
    route: `/api/revenue/setup-fee-plan-flow/get-all-portfolios/`,
  });

  const { mutate: mappedFeePlanPortfolio, isLoading } = useMutation({
    mutationKey: ["MAPPED_FEE_PLAN_PORTFOLIO"],
    mutationFn: (data) =>
      axiosInstance.post("/api/revenue/setup-fee-plan-flow/", data),
    onSuccess: () => {
      setOpen(false);
      refetchQuery("PORTFOLIO_FEE_PLAN_MAPPING_LIST");
    },
  });

  const form = useForm({
    defaultValues: {
      portfolio: [],
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio Name / Code (${portfolioFeePlanMappingList?.data?.data?.length})`,
        "portfolio",
        200,
        1,
      ),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Fee Plan", "feePlan", 200, 1),
      valueGetter: (params) => params?.row?.feePlan || "-",
    },
  ];

  return (
    <Grid2 container>
      <GKModal
        width="660px"
        open={open}
        setOpen={setOpen}
        onClose={() => setOpen(false)}
        modalTitle="Mapped Portfolios"
        maxHeight="calc(100vh - 237px)"
        footer={
          <Grid2 container justifyContent={"flex-end"}>
            <GKButton
              variant="contained"
              type="submit"
              loading={isLoading}
              disabled={isLoading || !form.formState.isDirty}
              form="portfolio-mapping-form"
              startIcon={<CheckIcon />}
            >
              Map Portfolio
            </GKButton>
          </Grid2>
        }
      >
        <form
          id="portfolio-mapping-form"
          onSubmit={(e) => {
            e.preventDefault();
            setConfirmationModal(true);
          }}
        >
          <Controller
            name="portfolio"
            control={form.control}
            render={({ field }) => (
              <GKClientDataGrid
                name="editFeePlanPortfolio"
                loading={portfolioMappingFetching}
                checkboxSelection
                onRowSelectionModelChange={(params) => {
                  field.onChange(params);
                }}
                rowSelectionModel={field.value}
                columns={columns}
                rows={portfolioFeePlanMappingList?.data?.data || []}
                maxHeight={"calc(100vh - 445px)"}
              />
            )}
          />
        </form>
      </GKModal>

      <ConfirmationModal
        open={confirmationModal}
        setOpen={setConfirmationModal}
        title="Map Portfolios"
        onConfirm={form.handleSubmit((values: any) =>
          mappedFeePlanPortfolio({
            ...values,
            feePlan: feePlanDetailsForm.getValues("id"),
          }),
        )}
        messageContent={
          <Typography>
            Some of the portfolios are already mapped to other fee plans. Are
            you sure you want to map all the selected portfolios to
            <Typography fontWeight={700}>
              {feePlanDetailsForm.getValues("name")} ?
            </Typography>
          </Typography>
        }
        customButtonText="Map"
      />
    </Grid2>
  );
};

export default MapPortfolioModal;
